import { Link } from '@tanstack/react-router'
import { ArrowRight, Terminal } from 'lucide-react'
import { buttonVariants } from '@/components/ui/Button'
import { type Product, type ProductStatus } from '@/constants/products'
import { cn } from '@/lib/cn'
import { ProductMediaViewport } from './ProductMediaViewport'

interface ProductVisionHeroProps {
  product: Product
}

const statusMap: Record<ProductStatus, { label: string; dotClass: string }> = {
  'Production Foundation': {
    label: 'Production Foundation',
    dotClass: 'bg-success',
  },
  'In Development': {
    label: 'Active Development',
    dotClass: 'bg-info',
  },
  'Planning Phase': {
    label: 'Architecture & Planning',
    dotClass: 'bg-fg-muted',
  },
  'Coming Soon': {
    label: 'Pre-Production',
    dotClass: 'bg-warning',
  },
}

export function ProductVisionHero({ product }: ProductVisionHeroProps) {
  const status = statusMap[product.status]

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center py-6 sm:py-8 lg:py-12 border-b border-border-subtle">
      {/* Left Column: Narrative & Technical Identity */}
      <div className="lg:col-span-7 space-y-6">
        {/* Category & Status Pulse */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="font-mono text-xs uppercase tracking-widest text-fg-muted px-2.5 py-1 rounded-xs bg-surface-2 border border-border-subtle">
            {product.category}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xs text-xs font-mono text-fg-subtle bg-surface-2 border border-border-subtle">
            <span
              className={cn('w-1.5 h-1.5 rounded-full animate-pulse', status.dotClass)}
              aria-hidden="true"
            />
            {status.label}
          </span>
        </div>

        {/* Product Title & Amharic Pill */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-fg">
              {product.name}
            </h1>
            {product.amharicName && (
              <span className="font-mono text-sm sm:text-base px-2.5 py-0.5 rounded-xs bg-surface-3 text-fg-subtle border border-border">
                {product.amharicName}
              </span>
            )}
          </div>
          <p className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-fg text-balance leading-snug">
            {product.actionStatement}
          </p>
        </div>

        {/* Deep Description */}
        <p className="text-sm sm:text-base text-fg-subtle leading-relaxed max-w-2xl">
          {product.description}
        </p>

        {/* Tech Stack Chips */}
        <div>
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted block mb-2">
            Engineering & Infrastructure Stack
          </span>
          <div className="flex flex-wrap gap-1.5">
            {product.techStack.map((tech) => (
              <span
                key={tech}
                className="font-mono text-xs px-2.5 py-1 rounded-xs bg-surface-2 text-fg border border-border-subtle"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Action CTAs */}
        <div className="pt-2 flex flex-wrap items-center gap-3">
          <Link
            to="/contact"
            className={buttonVariants({
              variant: 'primary',
              size: 'md',
              className: 'gap-2',
            })}
          >
            <span>Inquire About System</span>
            <ArrowRight className="w-4 h-4" />
          </Link>

          <a
            href="#engine-room"
            className={buttonVariants({
              variant: 'outline',
              size: 'md',
              className: 'gap-2',
            })}
          >
            <Terminal className="w-4 h-4 text-fg-muted" />
            <span>Inspect Engine</span>
          </a>
        </div>
      </div>

      {/* Right Column: Interactive Media Viewport */}
      <div className="lg:col-span-5 w-full">
        <ProductMediaViewport key={product.id} product={product} />
      </div>
    </div>
  )
}
